import type { Commodity } from '@enmon/commons/lib/shared';
import { createLlmPhotoRef, readLlmPhotoFieldName } from '@/features/inventorization/inventorize-photo-llm-form';
import { resizePhotoForLlm } from '@/features/inventorization/inventorize-photo-llm-image';
import {
  createInventoryAxiosClient,
  createLocationApiKeyAxiosClient,
  type InventoryAxiosClient,
} from '@/shared/api/default-axios-client';
import type { LocalPhotoRef } from '@/shared/capture/capture-session';
import type { InventorizeExtractionJobResponse } from '@/shared/ocr/inventorize-extraction-contract';

export { resizePhotoForLlm } from '@/features/inventorization/inventorize-photo-llm-image';

export interface LlmPhotoUpload {
  blob: Blob;
  photo: LocalPhotoRef;
}

export class InventorizePhotoLlmClient {
  private readonly axios: InventoryAxiosClient;

  constructor(input: { axiosClient?: InventoryAxiosClient } = {}) {
    this.axios = input.axiosClient ?? createInventoryAxiosClient();
  }

  async createJob(input: {
    commodity: Commodity;
    locationApiKey: string;
    photos: LlmPhotoUpload[];
    sessionId: string;
  }) {
    const formData = new FormData();
    formData.append(
      'data',
      JSON.stringify({
        commodity: input.commodity,
        photoRefs: input.photos.map((photo, index) => ({
          context: photo.photo.context,
          localPhotoId: photo.photo.id,
          photoRef: createLlmPhotoRef(photo.photo, index),
          registerId: photo.photo.registerId,
        })),
        sessionId: input.sessionId,
      }),
    );

    for (const [index, photo] of input.photos.entries()) {
      const resized = await resizePhotoForLlm(photo.blob);
      const fieldName = readLlmPhotoFieldName(photo.photo);
      formData.append(fieldName, resized, `${createLlmPhotoRef(photo.photo, index)}.jpg`);
    }

    const axios = createLocationApiKeyAxiosClient({
      axiosClient: this.axios,
      locationApiKey: input.locationApiKey,
    });
    const response = await axios.post<InventorizeExtractionJobResponse>(
      '/inventory-meters/inventorize/extraction-jobs',
      formData,
    );

    return response.data.data;
  }

  async getJob(input: { jobId: string; locationApiKey: string }) {
    const axios = createLocationApiKeyAxiosClient({
      axiosClient: this.axios,
      locationApiKey: input.locationApiKey,
    });
    const response = await axios.get<InventorizeExtractionJobResponse>(
      `/inventory-meters/inventorize/extraction-jobs/${encodeURIComponent(input.jobId)}`,
      {
        params: {
          _: Date.now(),
        },
      },
    );

    return response.data.data;
  }
}
